import { useEffect, useMemo, useState } from 'react';
import { Menu, Home, Plus, RefreshCw, LogOut, HelpCircle } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router';
import { queryClient } from '../../clients/queryClient';
import { toast } from '../../store/toast';
import { EToastType, TOAST_DURATIONS } from '../molecules/ToastMessage';
import { networkStatusAtom } from '../../store/network/networkState';
import { loadingInspectionMessageAtom } from '../../store/loadingInspectionProgressMessage';
import {
  inspectionQueryOptions,
  isPermissionDeniedError,
} from '../../store/inspection/inspectionAtoms';
import { inspectionService } from '../../services/inspection/inspection.service';
import {
  describeSyncResult,
  formatSyncTime,
} from '../../helpers/syncResultHelpers';
import type { InspectionSyncResult } from '../../types/inspection-sync';
import useLongPress from '../../hooks/useLongPress';
import { routePaths } from '../../routes/basePaths';
import { session, type SessionClaims } from '../../services/auth/session';
import { getDeviceType } from '../../utils/getDeviceType';
import { format } from 'date-fns';
import { useSignOut } from '../../hooks/useSignOut';
import useStaticImage from '../../hooks/useStaticImage';
import { getBuildNumber } from '../../utils/getBuildNumber';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
}
export function Drawer({ isOpen, onClose }: DrawerProps) {
  const [userInfo, setUserInfo] = useState<SessionClaims | null>();
  const [buildTime, setBuildTime] = useState<string>();
  const buildTimeJsonUrl = useStaticImage('build-time.json');
  // Vector wordmark rendered white via filter (the dedicated white PNG was
  // 88x15 and blurred when upscaled).
  const drawerLogoImage = useStaticImage('images/bradken_wordmark.svg');
  const navigate = useNavigate();
  const buildNumber = getBuildNumber();
  const isOnline = useAtomValue(networkStatusAtom);
  const syncProgress = useAtomValue(loadingInspectionMessageAtom);
  const { requestSignOut, signOutDialog } = useSignOut();

  const lastSyncDate = inspectionService.getLastSyncDate();

  // "Refresh data" = re-run the same delta-sync pipeline the app runs on
  // startup (inspectionQuery -> inspectionService.syncInspectionData).
  const showRefreshToast = (message: string, type: EToastType) =>
    toast.add({
      message,
      type,
      isOpen: true,
      options: { duration: TOAST_DURATIONS.VALIDATION },
    });
  const handleRefreshData = async () => {
    onClose();
    if (!isOnline) {
      showRefreshToast(
        'You are offline - connect to the internet to refresh data.',
        EToastType.warning,
      );
      return;
    }
    if (syncProgress.msg || syncProgress.isLoadingProgress) {
      showRefreshToast(
        'A data refresh is already running.',
        EToastType.warning,
      );
      return;
    }
    // fetchQuery, not refetchQueries: the query only exists while Home or
    // Support is mounted and is garbage-collected ten minutes after, when
    // refetchQueries matched nothing and still reported success. staleTime 0
    // because cached data under five minutes old would otherwise be returned
    // without a sync. A sync already in flight is joined, not repeated.
    let result: InspectionSyncResult;
    try {
      result = await queryClient.fetchQuery({
        ...inspectionQueryOptions,
        staleTime: 0,
      });
    } catch (error) {
      // The query's own error handler has already raised the Home banner.
      console.error('Refresh data failed:', error);
      showRefreshToast(
        isPermissionDeniedError(error)
          ? 'Your account is not configured for Insite Mobile - contact digital support.'
          : 'Could not refresh - check your connection and try again.',
        EToastType.warning,
      );
      return;
    }
    const { message, isWarning } = describeSyncResult(result);
    showRefreshToast(
      message,
      isWarning ? EToastType.warning : EToastType.success,
    );
  };
  const userProfile = useMemo(
    () => ({
      name: userInfo?.name || 'User',
      email: userInfo?.email || '',
      initials:
        userInfo?.name
          ?.split(' ')
          .filter(Boolean)
          .map((e) => e[0].toUpperCase()) || 'U',
      company: 'Bradken',
    }),
    [userInfo],
  );

  const onLongPress = (_: any) => {
    onClose();
    navigate(routePaths.SupportScreen);
  };

  // The short-press action lives INSIDE the hook so a long press can suppress
  // it. The button must NOT carry its own onClick - the native click that
  // follows mouseup/touchend used to override the easter-egg navigation.
  const onClick = () => {
    onClose();
    navigate(routePaths.HelpScreen);
  };

  const defaultOptions = {
    delay: 1500,
  };
  const longPressEvent = useLongPress({ onLongPress, onClick }, defaultOptions);

  useEffect(() => {
    setUserInfo(session.claims());
    fetch(buildTimeJsonUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        try {
          const buildTime = new Date(data.buildTime);
          setBuildTime(format(buildTime, 'dd MMM yy · hh:mma'));
          console.log(`App deployed at: ${buildTime.toLocaleString()}`);
        } catch (parseError) {
          // No fabricated fallback: showing NOW as the deploy time lied to
          // support in exactly the situations where they need the truth.
          console.error('Error parsing build time:', parseError);
        }
      })
      .catch((error) => {
        console.error('Error fetching build time:', error);
      });
  }, [buildTimeJsonUrl]);

  return (
    <>
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-300 ${
          isOpen
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0'
        }`}
      >
        <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
        <div
          className={`absolute top-0 left-0 flex h-full w-72 transform flex-col bg-primaryColor text-white shadow-lg transition-transform duration-300 ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Same-spot top row (insite's trick): this panel covers the app bar,
            and this row re-renders the bar's hamburger + wordmark white-on-navy
            at IDENTICAL coordinates (p-4 origin, w-8 h-8 button, gap-3, h-8
            wordmark - the geometry contract with Header.tsx). Opening the
            drawer reads as the chrome restyling around a stationary control;
            tapping the hamburger again closes it, so no separate X. */}
          <div className="flex items-center gap-3 border-b border-white/10 p-4">
            <button
              onClick={onClose}
              aria-label="Close navigation menu"
              className="flex size-8 shrink-0 items-center justify-center rounded-sm transition-colors hover:bg-white/10 active:bg-white/20"
            >
              <Menu className="size-6 text-white" />
            </button>
            <img
              src={drawerLogoImage}
              alt="Bradken"
              className="h-8 w-auto brightness-0 invert"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            {/* Header */}
            <div className="border-b border-white/10 p-4">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex size-12 items-center justify-center rounded-full bg-white">
                    <span className="font-bold text-primaryColor">
                      {userProfile.initials}
                    </span>
                  </div>
                  <div>
                    <h2 className="font-bold">{userProfile.name}</h2>
                    <p className="text-sm text-inkDisabled">
                      {userProfile.company}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-inkDisabled">
                <span className="text-sm">{userProfile.email}</span>
              </div>
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 py-4">
              <div className="space-y-2">
                {[
                  {
                    icon: <Home size={20} />,
                    label: 'Home',
                    onClick: () => {
                      // Close like every other item - navigating with the panel
                      // still open left it covering the destination.
                      onClose();
                      navigate(routePaths.Home);
                    },
                  },
                  {
                    icon: <Plus size={21} />,
                    label: 'New inspection',
                    onClick: () => {
                      onClose();
                      navigate(routePaths.NewInspection);
                    },
                  },
                  {
                    icon: <Plus size={21} />,
                    label: 'New bucket',
                    onClick: () => {
                      onClose();
                      navigate(routePaths.NewBucket);
                    },
                  },
                  {
                    icon: <RefreshCw size={20} />,
                    label: 'Refresh data',
                    detail: lastSyncDate
                      ? `Last synced ${formatSyncTime(lastSyncDate)}`
                      : 'Not synced yet',
                    onClick: () => void handleRefreshData(),
                  },
                ].map((item, index) => (
                  <button
                    key={index}
                    onClick={item.onClick}
                    className="flex w-full cursor-pointer items-center space-x-3 px-6 py-3 transition-colors hover:bg-white/10 active:bg-white/20"
                  >
                    {item.icon}
                    <span className="text-left">
                      <span className="block">{item.label}</span>
                      {item.detail && (
                        <span className="block text-xs text-inkDisabled">
                          {item.detail}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </nav>
          </div>
          {/* Footer - static, not absolute: the panel had no scroll and the
            absolute footer overlapped nav items on short landscape viewports. */}
          <div className="shrink-0 border-t border-white/10">
            <button
              className="flex w-full cursor-pointer items-center space-x-3 px-6 py-3 transition-colors hover:bg-white/10 active:bg-white/20"
              onClick={() => {
                onClose();
                void requestSignOut();
              }}
            >
              <LogOut size={20} />
              <span>Logout</span>
            </button>
            <button
              className="flex w-full cursor-pointer items-center space-x-3 px-6 py-3 transition-colors hover:bg-white/10 active:bg-white/20"
              {...longPressEvent}
            >
              <HelpCircle size={20} />
              <span>Help</span>
            </button>
            <div className="px-6 py-3 text-xs text-inkDisabled">
              {getDeviceType()}
              {buildTime ? `, ${buildTime}` : ''}
              {buildNumber && <div className="mt-1">Build #{buildNumber}</div>}
            </div>
          </div>
        </div>
      </div>
      {/* Outside the panel: it closes as Logout is pressed, and a closed panel is see-through and ignores taps. */}
      {signOutDialog}
    </>
  );
}
